import React, { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { usersAPI, authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { User, Shield, Loader2, KeyRound } from 'lucide-react';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [pwData, setPwData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await usersAPI.getProfile();
      setUser(response.data);
      setFormData({ name: response.data.name, phone: response.data.phone || '' });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.updateProfile(formData);
      toast.success('Profile updated successfully');
      setEditMode(false);
      loadProfile();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwData.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwData.new_password !== pwData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      await authAPI.changePassword({
        current_password: pwData.current_password,
        new_password: pwData.new_password,
      });
      toast.success('Password changed successfully');
      setPwData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const d = error?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const getKYCStatusVariant = (status) => {
    switch (status) {
      case 'verified': return 'default';
      case 'pending': return 'secondary';
      case 'rejected': return 'destructive';
      default: return 'outline';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Profile</h1>
          <p className="text-muted-foreground">Manage your account settings</p>
        </div>

        <div className="space-y-6">
          <Card data-testid="profile-card" className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-accent" />
              <h2 className="text-2xl font-semibold">Personal Information</h2>
            </div>

            {editMode ? (
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    data-testid="profile-name-input"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    data-testid="profile-phone-input"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="flex gap-3">
                  <Button data-testid="save-profile-btn" type="submit" variant="accent">Save Changes</Button>
                  <Button data-testid="cancel-edit-btn" type="button" variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <p className="text-lg" data-testid="profile-email">{user.email}</p>
                </div>
                <div>
                  <Label>Full Name</Label>
                  <p className="text-lg" data-testid="profile-name">{user.name}</p>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <p className="text-lg" data-testid="profile-phone">{user.phone || 'Not provided'}</p>
                </div>
                <div>
                  <Label>Member Since</Label>
                  <p className="text-lg" data-testid="profile-created">{new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <Button data-testid="edit-profile-btn" onClick={() => setEditMode(true)} variant="accent">Edit Profile</Button>
              </div>
            )}
          </Card>

          <Card data-testid="kyc-card" className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-accent" />
              <h2 className="text-2xl font-semibold">KYC Verification</h2>
              <Badge data-testid="kyc-status" variant={getKYCStatusVariant(user.kyc_status)} className="capitalize ml-auto">
                {user.kyc_status}
              </Badge>
            </div>

            {user.kyc_status === 'pending' ? (
              <div>
                <p className="text-muted-foreground mb-4">
                  Verify your identity to unlock higher transfer limits and additional features.
                </p>
                <Button data-testid="start-kyc-btn" variant="accent">Start Verification</Button>
              </div>
            ) : user.kyc_status === 'verified' ? (
              <p className="text-muted-foreground">Your account is verified. You can now enjoy full access to all features.</p>
            ) : (
              <p className="text-muted-foreground">Contact support for verification assistance.</p>
            )}
          </Card>

          <Card data-testid="change-password-card" className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <KeyRound className="w-6 h-6 text-accent" />
              <h2 className="text-2xl font-semibold">Change Password</h2>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  data-testid="current-password-input"
                  id="current-password"
                  type="password"
                  placeholder="••••••••"
                  value={pwData.current_password}
                  onChange={(e) => setPwData({ ...pwData, current_password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  data-testid="new-password-input"
                  id="new-password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={pwData.new_password}
                  onChange={(e) => setPwData({ ...pwData, new_password: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  data-testid="confirm-password-input"
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  value={pwData.confirm_password}
                  onChange={(e) => setPwData({ ...pwData, confirm_password: e.target.value })}
                  required
                />
              </div>
              <Button
                data-testid="change-password-btn"
                type="submit"
                variant="accent"
                disabled={pwLoading}
              >
                {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}